import { DateHandler } from '@angle/utils'
import { IQuoteFormData } from '../../pages/QuoteForm/QuoteForm'
import {
  ICensusDTO,
  ICensusMemberDTO,
  ICensusUpdateDTO,
  ICostShareDTO,
  ICreateGroupDTO,
  ICreateQuoteDTO,
  IGroupDTO,
  IMedicalPlanCreateDTO,
  IMedicalPlanDTO,
  IPastMedicalPlanCreateDTO,
  IPastMedicalPlanDTO,
  IPatchQuoteDTO,
  IQuoteDTO,
  IVendorDTO,
  ISoldQuoteDTO
} from './dto'
import { ICustomPlanForm } from '../../pages/QuoteForm/steps/PlanInfo/CustomPlanFormModal'
import { ICensusFormMember } from '../../pages/QuoteForm/steps/CensusPlanInfo/schemas'
import { IGroupForm } from '../../components/GroupFormModal/GroupFormModal'
import { IPreviousPlanForm } from '../../pages/QuoteForm/steps/QuoteDetails/previousPlanSchema'
import { IQuoteView } from '../../components/QuoteView/QuoteView'
import { unitStringFromCostShare } from '../../utils'
import { ISoldQuoteForm } from '../../components/SoldQuoteFormModal/SoldQuoteFormModal'

type DeepPartial<T> = T extends Array<infer U>
  ? Array<DeepPartial<U>>
  : T extends ReadonlyArray<infer U>
  ? ReadonlyArray<DeepPartial<U>>
  : T extends {
      [key in keyof T]: T[key]
    }
  ? {
      [K in keyof T]?: DeepPartial<T[K]>
    }
  : T

export const mapQuoteDtoToForm = (
  dto: IQuoteDTO
): DeepPartial<IQuoteFormData> => {

  const step2Obj: any = {}

  for (let program in dto.addOnPrograms) {
    step2Obj[`${program}ProgramIds`] = dto.addOnPrograms[program].map((vendor) => ({
      id: vendor.entryId
    }))
  }

  return {
    entryId: dto.entryId,
    basicInfo: {
      step1: {
        groupId: dto.group.entryId,
        effectiveDate: DateHandler.formatForClient(dto.effectiveDate),
        fundingType: dto.fundingType,
        pepmPayable: String(dto.pepmPayable),
        agentOfRecordName: dto.agentOfRecordName,
        agentOfRecordEmailAddress: dto.agentOfRecordEmailAddress,
        generalAgencyId: dto.generalAgencyId,
        generalAgencyName: dto.generalAgencyName,
      },
      step2: {
        networkId: dto.networkProgram?.entryId
      },
      step3: {
        pharmBenefitsManagerId: dto.pbmProgram?.entryId
      }
    },
    planInfo: {
      step1: {
        selectedPlans:
          dto.medicalPlanQuotes?.map((pq) => ({ id: pq.plan.entryId })) ?? []
      },
      step2: step2Obj
    },
    quoteDetails: {
      step1: {
        currentCoverageType:
          dto.prevFundingType === null ? 'No Current Coverage' : dto.prevFundingType
      },
      step2: {
        currentMonthlySpend: dto.prevMonthlyPremium?.toString() ?? null,
        renewalMonthlySpend: dto.renewalMonthlyPremium?.toString() ?? null,
        experienceFilesUploaded: dto.experienceInfoUploaded ?? false,
        monthlyInvoiceUploaded: dto.invoiceExampleUploaded ?? false,
        acknowledgment: dto.userAcceptedTerms ?? false
      }
    }
  }
}

// @ts-ignore
export const mapQuoteFormToCreateDTO = (
  formData: IQuoteFormData['basicInfo']['step1']
): ICreateQuoteDTO => ({
  groupId: formData.groupId,
  effectiveDate: DateHandler.formatForServer(formData.effectiveDate),
  fundingType: formData.fundingType,
  pepmPayable: parseFloat(String(formData.pepmPayable)?.replace(/[^\d,]+/, "")) || 0
})

export const mapQuoteFormToPatchDTO = (
  formData: IQuoteFormData
): IPatchQuoteDTO => {
  return {
    networkProgramId: formData.basicInfo.step2.networkId,
    pbmProgramId: formData.basicInfo.step3.pharmBenefitsManagerId,
    addOnIds: Object.values(formData.planInfo.step2).flatMap(arr => arr.map(({ id }) => id)),
    prevFundingType: formData.quoteDetails.step1.currentCoverageType,
    prevMonthlyPremium:
      formData.quoteDetails.step2.currentMonthlySpend === '' ||
      formData.quoteDetails.step2.currentMonthlySpend === null
        ? null
        : +formData.quoteDetails.step2.currentMonthlySpend,
    renewalMonthlyPremium:
      formData.quoteDetails.step2.renewalMonthlySpend === '' ||
      formData.quoteDetails.step2.renewalMonthlySpend === null
        ? null
        : +formData.quoteDetails.step2.renewalMonthlySpend,
    experienceInfoUploaded:
      formData.quoteDetails.step2.experienceFilesUploaded ?? false,
    invoiceExampleUploaded:
      formData.quoteDetails.step2.monthlyInvoiceUploaded ?? false,
    userAcceptedTerms: formData.quoteDetails.step2.acknowledgment ?? false
  }
}

export const mapQuoteFormForNetworkSelectionToPatchDTO = (
  formData: IQuoteFormData
): Partial<IPatchQuoteDTO> => {
  return {
    networkProgramId: formData.basicInfo.step2.networkId,
  }
}

export const mapQuoteFormForPbmProgramSelectionToPatchDTO = (
  formData: IQuoteFormData
): Partial<IPatchQuoteDTO> => {
  return {
    pbmProgramId: formData.basicInfo.step3.pharmBenefitsManagerId,
  }
}

export const mapQuoteFormAddOnsSelectionToPatchDTO = (
  formData: IQuoteFormData
): Partial<IPatchQuoteDTO> => {
  return {
    addOnIds: Object.values(formData.planInfo.step2).flatMap(arr => arr.map(({ id }) => id)),
  }
}

export const mapGroupFormToCreateDTO = (
  formData: IGroupForm
): ICreateGroupDTO => ({
  groupName: formData.groupName,
  city: formData.city,
  state: formData.state,
  zipCode: formData.zipCode,
  sicCode: formData.sicCode
})

export const mapGroupDTOToForm = (dto: IGroupDTO): IGroupForm => ({
  groupName: dto.groupName,
  sicCode: dto.sicCode,
  city: dto.city,
  state: dto.state as IGroupForm['state'],
  zipCode: dto.zipCode
})

const mapCostShareToFormValue = (
  costShare: ICostShareDTO
): ICustomPlanForm['services']['default'] => ({
  value:
    costShare.dataType === 'money' ? costShare.value : costShare.value * 100,
  type: costShare.dataType === 'money' ? 'copayment' : 'coinsurance'
})

export const mapMedicalPlanDTOToCustomForm = (
  plan: IMedicalPlanDTO
): ICustomPlanForm => ({
  planName: plan.planName,
  type: plan.isHsa ? 'HSA' : 'traditional',
  innDeductible: Number(plan.individualInnDeductible),
  oopDeductible: Number(plan.individualInnOopMax),
  services: {
    default: mapCostShareToFormValue(plan.costShareInnDefault),
    diagnostic: mapCostShareToFormValue(plan.costShareInnDiagnostic),
    emergencyRoom: mapCostShareToFormValue(plan.costShareInnEmergencyRoom),
    inPatient: mapCostShareToFormValue(plan.costShareInnInPatient),
    primaryCare: mapCostShareToFormValue(plan.costShareInnPrimaryCare),
    rxTier1: mapCostShareToFormValue(plan.costShareInnRx1),
    rxTier2: mapCostShareToFormValue(plan.costShareInnRx2),
    rxTier3: mapCostShareToFormValue(plan.costShareInnRx3),
    rxTier4: mapCostShareToFormValue(plan.costShareInnRx4),
    specialist: mapCostShareToFormValue(plan.costShareInnSpecialist),
    urgentCare: mapCostShareToFormValue(plan.costShareInnUrgentCare)
  }
})

const mapFormValuesToCostShare = (
  value: ICustomPlanForm['services']['default']
): ICostShareDTO => ({
  dataType: value.type === 'copayment' ? 'money' : 'one_based_percentage',
  value: value.type === 'copayment' ? value.value : value.value / 100
})

export const mapCustomPlanFormToDTO = (
  formValues: ICustomPlanForm
): IMedicalPlanCreateDTO => ({
  planName: formValues.planName,
  isHsa: formValues.type === 'HSA',
  individualInnDeductible: formValues.innDeductible,
  individualInnOopMax: formValues.oopDeductible,
  costShareInnDefault: mapFormValuesToCostShare(formValues.services.default),
  costShareInnDiagnostic: mapFormValuesToCostShare(
    formValues.services.diagnostic
  ),
  costShareInnEmergencyRoom: mapFormValuesToCostShare(
    formValues.services.emergencyRoom
  ),
  costShareInnInPatient: mapFormValuesToCostShare(
    formValues.services.inPatient
  ),
  costShareInnPrimaryCare: mapFormValuesToCostShare(
    formValues.services.primaryCare
  ),
  costShareInnRx1: mapFormValuesToCostShare(formValues.services.rxTier1),
  costShareInnRx2: mapFormValuesToCostShare(formValues.services.rxTier2),
  costShareInnRx3: mapFormValuesToCostShare(formValues.services.rxTier3),
  costShareInnRx4: mapFormValuesToCostShare(formValues.services.rxTier4),
  costShareInnSpecialist: mapFormValuesToCostShare(
    formValues.services.specialist
  ),
  costShareInnUrgentCare: mapFormValuesToCostShare(
    formValues.services.urgentCare
  )
})

export const mapCensusInfoToDTO = (
  info: ICensusFormMember[]
): ICensusUpdateDTO => {
  const mapFamilyTier = (
    tier: ICensusFormMember['familyTier']
  ): ICensusMemberDTO['familyTier'] => {
    switch (tier) {
      case '':
        return null
      default:
        return tier
    }
  }

  return {
    members: info.map((formMember) => ({
      firstName: formMember.firstName,
      lastName: formMember.lastName,
      dateOfBirth: DateHandler.formatForServer(formMember.dateOfBirth),
      zipCode: formMember.zipCode,
      relationship: formMember.relationship,
      gender: formMember.gender,
      familyTier: mapFamilyTier(formMember.familyTier),
      prevPlanName: formMember.prevPlanName ?? null
    }))
  }
}

export const mapCensusDTOtoForm = (
  members: ICensusDTO
): ICensusFormMember[] => {
  const mapFamilyTier = (
    tier: ICensusMemberDTO['familyTier']
  ): ICensusFormMember['familyTier'] => {
    switch (tier) {
      case null:
        return ''
      default:
        return tier
    }
  }

  if (!Array.isArray(members)) {
    return []
  }

  return (
    members?.map((member) => ({
      firstName: member.firstName,
      lastName: member.lastName,
      dateOfBirth: DateHandler.formatForClient(member.dateOfBirth),
      gender: member.gender,
      relationship: member.relationship,
      familyTier: mapFamilyTier(member.familyTier),
      zipCode: member.zipCode,
      prevPlanName: member.prevPlanName ?? undefined
    })) ?? []
  )
}

export const mapDTOToPastMedPlanForm = (
  dto: IPastMedicalPlanDTO
): IPreviousPlanForm => {
  const getPercentFromDecimal = (val: number | null) => {
    const coinsuranceNum = Number(val)
    if (!isNaN(coinsuranceNum)) {
      return (coinsuranceNum * 100).toString()
    }
    return val?.toString() ?? ''
  }

  return {
    id: dto.entryId,
    planName: dto.planName,
    type: dto.isHsa ? 'HSA' : 'traditional',
    indInnDeductible: dto.individualInnDeductible ?? 0,
    indInnOOP: dto.individualInnOopMax ?? 0,
    memberCoinsurance: getPercentFromDecimal(dto.coinsurance),
    enrollmentCount: {
      employeeOnly: dto.singleEnrollmentCount?.toString() ?? null,
      employeePlusSpouse: dto.employeeSpouseEnrollmentCount?.toString() ?? null,
      employeePlusChildren:
        dto.employeeChildrenEnrollmentCount?.toString() ?? null,
      employeePlusFamily: dto.familyEnrollmentCount?.toString() ?? null
    },
    rates: {
      employeeOnly: dto.singleCurrentRate?.toString() ?? null,
      employeePlusSpouse: dto.employeeSpouseCurrentRate?.toString() ?? null,
      employeePlusChildren: dto.employeeChildrenCurrentRate?.toString() ?? null,
      employeePlusFamily: dto.familyCurrentRate?.toString() ?? null
    },
    renewalRates: {
      employeeOnly: dto.singleRenewalRate?.toString() ?? null,
      employeePlusSpouse: dto.employeeSpouseRenewalRate?.toString() ?? null,
      employeePlusChildren: dto.employeeChildrenRenewalRate?.toString() ?? null,
      employeePlusFamily: dto.familyRenewalRate?.toString() ?? null
    }
  }
}

export const mapPastMedPlanFormToDTO = (
  formData: IPreviousPlanForm
): IPastMedicalPlanCreateDTO => {
  const getDecimalFromPercent = (val: string | null) => {
    const coinsuranceNum = Number(val)
    if (!isNaN(coinsuranceNum)) {
      return (coinsuranceNum / 100).toString()
    }
    return val ?? ''
  }

  return {
    planName: formData.planName,
    isHsa: formData.type === 'HSA',
    individualInnDeductible: +formData.indInnDeductible,
    individualInnOopMax: +formData.indInnOOP,
    coinsurance: +getDecimalFromPercent(formData.memberCoinsurance),
    singleEnrollmentCount: formData.enrollmentCount.employeeOnly
      ? Number(formData.enrollmentCount.employeeOnly)
      : null,
    employeeSpouseEnrollmentCount: formData.enrollmentCount.employeePlusSpouse
      ? Number(formData.enrollmentCount.employeePlusSpouse)
      : null,
    employeeChildrenEnrollmentCount: formData.enrollmentCount
      .employeePlusChildren
      ? Number(formData.enrollmentCount.employeePlusChildren)
      : null,
    familyEnrollmentCount: formData.enrollmentCount.employeePlusFamily
      ? Number(formData.enrollmentCount.employeePlusFamily)
      : null,
    singleCurrentRate: formData.rates.employeeOnly
      ? Number(formData.rates.employeeOnly)
      : null,
    employeeSpouseCurrentRate: formData.rates.employeePlusSpouse
      ? Number(formData.rates.employeePlusSpouse)
      : null,
    employeeChildrenCurrentRate: formData.rates.employeePlusChildren
      ? Number(formData.rates.employeePlusChildren)
      : null,
    familyCurrentRate: formData.rates.employeePlusFamily
      ? Number(formData.rates.employeePlusFamily)
      : null,
    singleRenewalRate: formData.renewalRates.employeeOnly
      ? Number(formData.renewalRates.employeeOnly)
      : null,
    employeeSpouseRenewalRate: formData.renewalRates.employeePlusSpouse
      ? Number(formData.renewalRates.employeePlusSpouse)
      : null,
    employeeChildrenRenewalRate: formData.renewalRates.employeePlusChildren
      ? Number(formData.renewalRates.employeePlusChildren)
      : null,
    familyRenewalRate: formData.renewalRates.employeePlusFamily
      ? Number(formData.renewalRates.employeePlusFamily)
      : null
  }
}

export const mapQuoteDtoToQuoteView = (
  quoteDto: IQuoteDTO,
  previousPlansAdded: boolean
): IQuoteView => {
  const mapAddOns = (programs: IVendorDTO[], category: string) =>
    programs.map((prog) => ({
      name: prog.programName,
      category,
      image: prog.logoUrl
    }))

  const addOns = []

  for (let program in quoteDto.addOnPrograms) {
    addOns.push(...mapAddOns(quoteDto.addOnPrograms[program], program))
  }

  return {
    addOns: addOns,
    network: {
      name: quoteDto.networkProgram?.programName ?? '',
      image: quoteDto.networkProgram?.logoUrl ?? ''
    },
    pharmacy: {
      name: quoteDto.pbmProgram?.programName ?? '',
      image: quoteDto.pbmProgram?.logoUrl ?? ''
    },
    plans:
      quoteDto.medicalPlanQuotes?.map(({ plan, rates }) => ({
        deductibleType:
          plan.deductibleType === 'level_funded'
            ? 'Level Funded'
            : 'Fully Insured',
        famDeductible: `$${plan.deductibleFamilyInn}`,
        famOopMax: `$${plan.oopMaxFamilyInn}`,
        id: plan.entryId.toString(),
        innDeductible: `$${plan.individualInnDeductible}`,
        innOopMax: `$${plan.individualInnOopMax}`,
        isCreditable: plan.isCreditable,
        isHsa: plan.isHsa,
        name: plan.planName,
        OON: {
          famDeductible: `$${plan.deductibleFamilyOon}`,
          OOPMFam: `$${plan.oopmFamilyOon}`,
          indDeductible: `$${plan.deductibleIndividualOon}`,
          OOPMInd: `$${plan.oopmIndividualOon}`,
          coinsurance: `${plan.coinsuranceOon * 100}%`,
          deductibleEmbedded: plan.isEmbedded
        },
        rates: rates?.Employee
          ? {
              name: plan.planName,
              employeeOnly: `$${rates?.Employee}`,
              employeePlusSpouse: `$${rates?.['Employee+Spouse']}`,
              employeePlusChildren: `$${rates?.['Employee+Children']}`,
              employeePlusFamily: `$${rates?.['Employee+Family']}`
            }
          : undefined,
        services: {
          default: unitStringFromCostShare(plan.costShareInnDefault),
          diagnostic: unitStringFromCostShare(plan.costShareInnDiagnostic),
          emergencyRoom: unitStringFromCostShare(
            plan.costShareInnEmergencyRoom
          ),
          inPatient: unitStringFromCostShare(plan.costShareInnInPatient),
          primaryCare: unitStringFromCostShare(plan.costShareInnPrimaryCare),
          rxTier1: unitStringFromCostShare(plan.costShareInnRx1),
          rxTier2: unitStringFromCostShare(plan.costShareInnRx2),
          rxTier3: unitStringFromCostShare(plan.costShareInnRx3),
          rxTier4: unitStringFromCostShare(plan.costShareInnRx4),
          specialist: unitStringFromCostShare(plan.costShareInnSpecialist),
          urgentCare: unitStringFromCostShare(plan.costShareInnUrgentCare)
        },
        type: plan.isHsa ? 'HDHP (HSA Eligible)' : 'Traditional'
      })) ?? [],
    previousInfo: {
      planAdded: previousPlansAdded,
      experienceProvided: quoteDto.experienceInfoUploaded,
      invoiceInfoAdded: quoteDto.invoiceExampleUploaded
    },
    previousQuote: quoteDto.previousQuote,
    quoteDetails: {
      groupName: quoteDto.group.groupName,
      platformQuoted: quoteDto.platformQuoted,
      agency: quoteDto.agency,
      cityAndState: `${quoteDto.group.city}, ${quoteDto.group.state}`,
      zipCode: quoteDto.group.zipCode,
      pepmPayable: quoteDto.pepmPayable,
      effectiveDate: DateHandler.formatForClient(quoteDto.effectiveDate),
      fundingType:
        quoteDto.fundingType === 'level_funded'
          ? 'Level Funded'
          : 'Fully Insured'
    },
    quoteId: quoteDto.entryId.toString()
  }
}

export const mapSoldQuoteFormToCreateDTO = (
  formData: ISoldQuoteForm
): ISoldQuoteDTO => ({
  groupName: formData.groupName,
  effectiveDate: DateHandler.formatForServer(formData.effectiveDate),
  groupContactFirstName: formData.groupContactFirstName,
  groupContactLastName: formData.groupContactLastName
})

export const mapSoldQuoteDTOToForm = (dto: IQuoteDTO): ISoldQuoteForm => ({
  groupName: dto.group.groupName,
  effectiveDate: DateHandler.formatForClient(dto.effectiveDate),
  groupContactFirstName: '',
  groupContactLastName: ''
})
